import React, { useContext } from "react";
import {
  EuiAccordion,
  EuiBadge,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiMarkdownFormat,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { type Hierarchical } from "anketa-core";
import { EvaluationContext, useAnketa } from "../context_provider";
import { countIssues } from "../issues";
import { useAnketaI18n } from "src/internationalization";
import { RowCardProps } from "src/questionnaires/types";
import { cardOptions, fieldGroupOf, isCard } from "../presentation";

/**
 * Card layout for a Row marked with `presentAsCard`: bordered panel, title,
 * and the fields in an aligned grid, bucketed under their field-group
 * headings. Nested rows become full-width sub-panels. With `collapsible`, the
 * body folds behind a header that still shows a summary, status chips and
 * how many fields are open - so a long list of nodes stays scannable.
 */
const CardRow = ({ row, nested }: RowCardProps): JSX.Element => {
  const ctx = useAnketa();
  const { revealIssues } = useContext(EvaluationContext);
  const { translate } = useAnketaI18n();
  const options = cardOptions(row);
  const collapsibleGroups = options.collapsibleGroups !== false;

  // Bucket consecutive visible children by their field group. Hidden children
  // are dropped first so a group whose fields are all hidden gets no heading.
  const buckets: Array<{
    key: string;
    label?: string;
    children: Hierarchical[];
  }> = [];
  row.children.forEach((child: Hierarchical) => {
    if ((child as { visible?: boolean }).visible === false) {
      return;
    }
    const group = fieldGroupOf(child);
    const key = group?.key ?? "";
    const last = buckets[buckets.length - 1];
    if (last && last.key === key) {
      last.children.push(child);
    } else {
      buckets.push({
        key,
        label: group ? ctx.i18nContext.render(group.label) : undefined,
        children: [child],
      });
    }
  });

  const body = buckets.map((bucket, i) => {
    const grid = (
      <div className="aqCard__grid">
        {bucket.children.map((child) => ctx.childElement(row, child))}
      </div>
    );
    const key = `${row.path}.${bucket.key}.${i}`;
    if (!bucket.label) {
      return (
        <div className="aqCard__group" key={key}>
          {grid}
        </div>
      );
    }
    if (!collapsibleGroups) {
      return (
        <div className="aqCard__group" key={key}>
          <EuiText size="xs" color="subdued" className="aqCard__groupLabel">
            <h5>{bucket.label}</h5>
          </EuiText>
          {grid}
        </div>
      );
    }
    // Groups fold individually (e.g. put Hardware away once it is filled in).
    return (
      <div className="aqCard__group" key={key}>
        <EuiAccordion
          id={`${row.path}-group-${bucket.key}`}
          initialIsOpen
          paddingSize="s"
          buttonContent={
            <EuiText size="xs" color="subdued" className="aqCard__groupLabel">
              <h5>{bucket.label}</h5>
            </EuiText>
          }
        >
          {grid}
        </EuiAccordion>
      </div>
    );
  });

  const title = ctx.i18nContext.render(row.title);
  const issues = countIssues(row, ctx);
  const open = issues.missing + issues.invalid;
  const summary = options.summary?.();
  const badges = options.badges?.() ?? [];

  const header = (
    <EuiFlexGroup
      gutterSize="s"
      alignItems="center"
      responsive={false}
      wrap
      className="aqCard__header"
    >
      <EuiFlexItem grow={false}>
        <EuiTitle size={nested ? "xxs" : "xs"}>
          <h4>{title}</h4>
        </EuiTitle>
      </EuiFlexItem>
      {badges.map((badge, i) => (
        <EuiFlexItem grow={false} key={`badge-${i}`}>
          <EuiBadge color={badge.color ?? "default"}>
            {ctx.i18nContext.render(badge.label)}
          </EuiBadge>
        </EuiFlexItem>
      ))}
      {open > 0 && (
        <EuiFlexItem grow={false}>
          <EuiBadge
            color={issues.invalid > 0 && revealIssues ? "danger" : "hollow"}
            title={translate("anketa.questionnaire.tab.open", {
              values: { count: open },
              defaultMessage: "{{count}} fields still open",
            })}
          >
            {translate("anketa.questionnaire.card.open", {
              values: { count: open },
              defaultMessage: "{{count}} open",
            })}
          </EuiBadge>
        </EuiFlexItem>
      )}
      {summary && (
        <EuiFlexItem grow={false} style={{ minWidth: 0 }}>
          <EuiText size="s" color="subdued">
            <span className="aqReadonlyValue">{summary}</span>
          </EuiText>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  );

  const description = row.description ? (
    <>
      <EuiText size="s" color="subdued">
        <EuiMarkdownFormat>
          {ctx.i18nContext.render(row.description)}
        </EuiMarkdownFormat>
      </EuiText>
      <EuiSpacer size="s" />
    </>
  ) : null;

  const panelProps = {
    hasBorder: !nested,
    hasShadow: false,
    color: (nested ? "subdued" : "plain") as "subdued" | "plain",
    className: nested ? "aqCard aqCard--nested" : "aqCard",
  };

  if (options.collapsible) {
    // Default: open while something needs an answer, closed when complete.
    const initialIsOpen = options.initialIsOpen
      ? options.initialIsOpen()
      : open > 0;
    return (
      <EuiPanel {...panelProps} paddingSize="s">
        <EuiAccordion
          id={`${row.path}-card`}
          initialIsOpen={initialIsOpen}
          buttonContent={header}
          buttonClassName="aqCard__toggle"
          paddingSize="s"
        >
          {description}
          {body}
        </EuiAccordion>
      </EuiPanel>
    );
  }

  return (
    <EuiPanel {...panelProps} paddingSize="m">
      {header}
      <EuiSpacer size="s" />
      {description}
      {body}
    </EuiPanel>
  );
};

export const RowCard = ({ row, nested }: RowCardProps): JSX.Element => {
  const ctx = useAnketa();
  if (!row.visible) {
    return <div></div>;
  }
  if (isCard(row)) {
    return (
      <>
        <CardRow row={row} nested={nested} />
        {!nested && <EuiSpacer size="m" />}
      </>
    );
  }
  return (
    <EuiFlexGroup wrap={true} direction="column">
      <EuiSpacer />
      <EuiFlexItem>
        <EuiText>
          <h4>{ctx.i18nContext.render(row.title)}</h4>
        </EuiText>
      </EuiFlexItem>
      {row.description && (
        <EuiFlexItem>
          <EuiCallOut>
            <EuiMarkdownFormat>
              {ctx.i18nContext.render(row.description)}
            </EuiMarkdownFormat>
          </EuiCallOut>
        </EuiFlexItem>
      )}
      <EuiFlexGroup wrap={true} key={row.id + ".children"}>
        {ctx.childrenForItem(row)}
      </EuiFlexGroup>
      <EuiSpacer />
    </EuiFlexGroup>
  );
};
